// Organic / Fluid landing page — monolithic iframe block
// 4 brand variants: botanica / ocea / noir / pulse

export type OrganicLandingBrand = 'botanica' | 'ocea' | 'noir' | 'pulse'
export interface OrganicLandingPageData { brand: OrganicLandingBrand }
export const DEFAULT_ORGANIC_LANDING_DATA: OrganicLandingPageData = { brand: 'botanica' }

// ── CSS ───────────────────────────────────────────────────────────────────────
const ORGANIC_LANDING_CSS = `
/* ============ DESIGN TOKENS / 4 BRAND THEMES ============ */
:root{--ease:cubic-bezier(.2,.7,.2,1)}
html[data-theme="botanica"]{
  --bg:#F4EFE4;--bg2:#EAE2D0;--card:#FCFAF3;--ink:#1E2A21;--muted:#5D6757;
  --accent:#C4623D;--accent-ink:#FFF8F0;--accent2:#75886D;--line:rgba(30,42,33,.15);
  --font-display:'Fraunces',serif;--font-body:'Manrope',sans-serif;--shadow:rgba(30,42,33,.14);
}
html[data-theme="ocea"]{
  --bg:#EAF4F2;--bg2:#DBEBE7;--card:#F7FBFA;--ink:#0B343D;--muted:#4E6B70;
  --accent:#0F7E7B;--accent-ink:#F2FBF9;--accent2:#E98A54;--line:rgba(11,52,61,.15);
  --font-display:'Sora',sans-serif;--font-body:'Manrope',sans-serif;--shadow:rgba(11,52,61,.14);
}
html[data-theme="noir"]{
  --bg:#131117;--bg2:#1B1922;--card:#211E2A;--ink:#F2ECDF;--muted:#9A948A;
  --accent:#C9A15C;--accent-ink:#17130B;--accent2:#8C8798;--line:rgba(242,236,223,.16);
  --font-display:'Cormorant Garamond',serif;--font-body:'Jost',sans-serif;--shadow:rgba(0,0,0,.5);
}
html[data-theme="pulse"]{
  --bg:#FFF3E6;--bg2:#FFE6CD;--card:#FFF9F1;--ink:#221733;--muted:#6B5E7A;
  --accent:#FF4D2A;--accent-ink:#FFF5EE;--accent2:#7A4FD3;--line:rgba(34,23,51,.15);
  --font-display:'Unbounded',sans-serif;--font-body:'Space Grotesk',sans-serif;--shadow:rgba(34,23,51,.14);
}
/* ============ BASE ============ */
*{margin:0;padding:0;box-sizing:border-box}
html{scroll-behavior:smooth}
body{background:var(--bg);color:var(--ink);font-family:var(--font-body);font-size:16.5px;line-height:1.65;overflow-x:hidden;transition:background .5s ease,color .5s ease}
img{max-width:100%;display:block}
a{color:inherit;text-decoration:none}
button{font-family:inherit;cursor:pointer}
h1,h2,h3,.display{font-family:var(--font-display)}
::selection{background:var(--accent);color:var(--accent-ink)}
:focus-visible{outline:2px solid var(--accent);outline-offset:3px;border-radius:4px}
.container{width:min(1180px,92vw);margin-inline:auto}
section{scroll-margin-top:96px}
em{color:var(--accent);font-style:italic}
html[data-theme="ocea"] em,html[data-theme="pulse"] em{font-style:normal}
/* noise overlay */
.noise{position:fixed;inset:0;z-index:500;pointer-events:none;opacity:.055;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='220' height='220'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.7'/%3E%3C/svg%3E")}
/* progress */
#progress{position:fixed;top:0;left:0;height:3px;width:0;background:var(--accent);z-index:460;border-radius:0 3px 3px 0}
/* theme wipe */
#wipe{position:fixed;inset:0;z-index:400;display:grid;place-items:center;pointer-events:none}
#wipeBlob{width:130vmax;height:130vmax;background:#1E2A21;border-radius:42% 58% 55% 45%/52% 48% 55% 45%;transform:scale(0);transition:transform .45s cubic-bezier(.7,0,.3,1)}
#wipe.on #wipeBlob{transform:scale(1)}
/* reveal */
.reveal{opacity:0;transform:translateY(30px);transition:opacity .8s ease,transform .8s var(--ease)}
.reveal.in{opacity:1;transform:none}
/* ============ HEADER ============ */
header{position:fixed;top:0;left:0;right:0;z-index:300;padding:18px 0;transition:padding .35s,background .35s,border-color .35s;border-bottom:1px solid transparent}
header.scrolled{padding:10px 0;background:var(--bg);border-bottom-color:var(--line)}
.header-in{display:flex;align-items:center;justify-content:space-between;gap:24px}
.logo{display:flex;align-items:center;gap:12px}
.logo-blob{width:44px;height:44px;background:var(--accent);color:var(--accent-ink);display:grid;place-items:center;font-family:var(--font-display);font-weight:700;font-size:1.15rem;animation:morph 9s ease-in-out infinite alternate;transition:background .5s}
.logo-txt{line-height:1.1}
.logo-txt strong{font-family:var(--font-display);font-size:1.3rem;letter-spacing:.04em}
.logo-txt span{display:block;font-size:.62rem;letter-spacing:.3em;text-transform:uppercase;color:var(--muted)}
.main-nav{display:flex;gap:26px}
.main-nav a{font-size:.86rem;font-weight:600;letter-spacing:.06em;position:relative;padding:6px 0}
.main-nav a::after{content:"";position:absolute;left:0;bottom:0;height:2px;width:0;background:var(--accent);border-radius:2px;transition:width .3s var(--ease)}
.main-nav a:hover::after{width:100%}
.btn{display:inline-flex;align-items:center;gap:.6rem;padding:.95rem 1.8rem;border-radius:999px;background:var(--accent);color:var(--accent-ink);font-weight:700;font-size:.95rem;border:1px solid transparent;transition:transform .3s var(--ease),box-shadow .3s,background .3s;will-change:transform}
.btn:hover{transform:translateY(-3px);box-shadow:0 14px 30px var(--shadow)}
.btn:active{transform:scale(.97)}
.btn-ghost{background:transparent;color:var(--ink);border-color:var(--line)}
.btn-ghost:hover{border-color:var(--accent);color:var(--accent)}
.btn-sm{padding:.6rem 1.25rem;font-size:.85rem}
.btn-big{padding:1.15rem 2.6rem;font-size:1.05rem}
.link-arrow{font-weight:700;font-size:.95rem;color:var(--ink);border-bottom:2px solid var(--accent);padding-bottom:3px;transition:color .3s}
.link-arrow:hover{color:var(--accent)}
.burger{display:none;width:46px;height:46px;border:1px solid var(--line);border-radius:50%;background:transparent;position:relative;z-index:310}
.burger span{position:absolute;left:13px;right:13px;height:2px;background:var(--ink);transition:transform .35s var(--ease),background .3s}
.burger span:nth-child(1){top:18px}.burger span:nth-child(2){top:26px}
.menu-open .burger span:nth-child(1){transform:translateY(4px) rotate(45deg)}
.menu-open .burger span:nth-child(2){transform:translateY(-4px) rotate(-45deg)}
.menu-open .burger span{background:var(--bg)}
/* mobile nav */
#mnav{position:fixed;inset:0;z-index:290;background:var(--ink);color:var(--bg);transform:translateY(-102%);transition:transform .55s cubic-bezier(.7,0,.3,1);display:flex;flex-direction:column;justify-content:center;padding:100px 8vw 60px}
.menu-open #mnav{transform:translateY(0)}
#mnav ul{list-style:none}
#mnav li{opacity:0;transform:translateY(24px);transition:opacity .4s,transform .4s var(--ease)}
.menu-open #mnav li{opacity:1;transform:none}
.menu-open #mnav li:nth-child(1){transition-delay:.15s}.menu-open #mnav li:nth-child(2){transition-delay:.22s}.menu-open #mnav li:nth-child(3){transition-delay:.29s}.menu-open #mnav li:nth-child(4){transition-delay:.36s}.menu-open #mnav li:nth-child(5){transition-delay:.43s}
#mnav a.mlink{font-family:var(--font-display);font-size:clamp(2rem,8vw,3.1rem);display:flex;align-items:baseline;gap:14px;padding:10px 0}
#mnav a.mlink i{font-style:normal;font-size:.8rem;color:var(--accent);letter-spacing:.2em}
#mnav a.mlink:hover{color:var(--accent)}
#mnav .mnav-foot{margin-top:auto;color:var(--bg);opacity:.7;font-size:.9rem}
/* ============ HERO ============ */
.hero{padding:170px 0 90px;position:relative}
.hero-grid{display:grid;grid-template-columns:1.05fr .95fr;gap:56px;align-items:center}
.eyebrow{display:inline-flex;align-items:center;gap:10px;font-size:.75rem;font-weight:800;letter-spacing:.24em;text-transform:uppercase;color:var(--muted)}
.eyebrow::before{content:"";width:9px;height:9px;border-radius:50%;background:var(--accent);flex:none}
.hero h1{font-size:clamp(2.7rem,6.2vw,5.3rem);line-height:1.02;font-weight:900;letter-spacing:-.02em;margin:22px 0 24px}
html[data-theme="noir"] .hero h1{font-weight:600;font-size:clamp(3rem,7.2vw,6.1rem);letter-spacing:0}
html[data-theme="pulse"] .hero h1{font-weight:900;font-size:clamp(1.9rem,4.6vw,3.7rem);line-height:1.1}
html[data-theme="ocea"] .hero h1{font-weight:800;letter-spacing:-.03em}
.lead{color:var(--muted);max-width:520px;font-size:1.06rem}
.hero-actions{display:flex;align-items:center;gap:26px;flex-wrap:wrap;margin:32px 0 26px}
.chips{display:flex;gap:10px;flex-wrap:wrap}
.chip{display:inline-flex;align-items:center;gap:8px;padding:.45rem 1rem;border:1px solid var(--line);border-radius:999px;font-size:.8rem;font-weight:700;background:var(--card);transition:border-color .3s,transform .3s}
.chip:hover{border-color:var(--accent);transform:translateY(-2px)}
.chip::before{content:"";width:7px;height:7px;border-radius:50%;background:var(--accent2)}
.hero-visual{position:relative;justify-self:center;width:min(480px,100%)}
.blob-frame{aspect-ratio:1/1.06;overflow:hidden;border:1px solid var(--line);box-shadow:0 30px 70px var(--shadow);animation:morph 11s ease-in-out infinite alternate;background:var(--card)}
.blob-frame img{width:100%;height:100%;object-fit:cover;animation:kenburns 16s ease-in-out infinite alternate;transition:opacity .45s ease}
.blob-frame img.fade{opacity:0}
.deco-blob{position:absolute;z-index:-1;filter:blur(2px);opacity:.5;animation:morph 8s ease-in-out infinite alternate}
.deco-blob.b1{width:180px;height:180px;background:var(--accent2);top:-40px;left:-56px}
.deco-blob.b2{width:130px;height:130px;background:var(--accent);bottom:-24px;right:-34px;opacity:.35;animation-delay:-3s}
.stamp{position:absolute;top:-34px;right:-26px;width:118px;height:118px;background:var(--card);border:1px solid var(--line);border-radius:50%;display:grid;place-items:center;box-shadow:0 12px 30px var(--shadow)}
.stamp svg{width:96px;height:96px;animation:spin 18s linear infinite}
.stamp text{font-size:10.5px;letter-spacing:2.6px;font-weight:700;fill:var(--ink);font-family:var(--font-body)}
.hero-badge{position:absolute;left:-22px;bottom:44px;background:var(--card);border:1px solid var(--line);border-radius:999px;padding:.6rem 1.1rem;font-size:.8rem;font-weight:800;box-shadow:0 14px 34px var(--shadow);display:flex;align-items:center;gap:9px;animation:floaty 5s ease-in-out infinite}
.hero-badge::before{content:"";width:8px;height:8px;border-radius:50%;background:var(--accent);animation:pulse 2s infinite}
/* ============ MARQUEE ============ */
.mq{background:var(--ink);color:var(--bg);overflow:hidden;padding:18px 0;transition:background .5s}
.mq-inner{display:flex;width:max-content;animation:mqmove 30s linear infinite}
.mq:hover .mq-inner{animation-play-state:paused}
.mq-track{display:flex;align-items:center;white-space:nowrap;font-family:var(--font-display);font-size:1.05rem;letter-spacing:.12em;text-transform:uppercase}
.mq-track span{padding:0 18px}
.mq-track i{font-style:normal;color:var(--accent)}
html[data-theme="noir"] .mq-track i{color:var(--accent)}
/* ============ MANIFESTO ============ */
#manifest{padding:110px 0 100px;position:relative}
.manifest-text{font-family:var(--font-display);font-size:clamp(1.45rem,3vw,2.35rem);line-height:1.4;max-width:900px;margin-top:26px;font-weight:600}
html[data-theme="pulse"] .manifest-text{font-weight:700;font-size:clamp(1.15rem,2.5vw,1.9rem)}
.stats{display:grid;grid-template-columns:repeat(4,1fr);gap:26px;margin-top:64px}
.stat{border-top:2px solid var(--line);padding-top:20px;transition:border-color .3s}
.stat:hover{border-color:var(--accent)}
.stat-n{font-family:var(--font-display);font-size:clamp(2.2rem,4vw,3.2rem);font-weight:900;color:var(--accent);line-height:1}
html[data-theme="noir"] .stat-n{font-weight:600}
.stat-l{color:var(--muted);font-size:.88rem;margin-top:10px;line-height:1.45}
/* waves */
.wave{display:block;line-height:0;margin-top:-1px}
.wave svg{width:100%;height:74px;display:block}
.wave.w1{background:var(--bg);color:var(--bg2)}
.wave.w2{background:var(--bg);color:var(--bg2)}
.wave.w3{background:var(--bg2);color:var(--ink)}
.wave.w4{background:var(--ink);color:var(--bg)}
/* ============ APPROACH (sticky 2-col) ============ */
#approach{background:var(--bg2);padding:100px 0 120px;transition:background .5s}
.ap-grid{display:grid;grid-template-columns:.9fr 1.1fr;gap:70px;align-items:start}
.ap-left{position:sticky;top:120px}
.ap-left h2{font-size:clamp(2rem,4vw,3.3rem);line-height:1.08;margin:20px 0 18px;font-weight:900}
html[data-theme="noir"] .ap-left h2{font-weight:600}
html[data-theme="pulse"] .ap-left h2{font-size:clamp(1.5rem,3.2vw,2.4rem)}
.ap-note{color:var(--muted);max-width:380px;margin-bottom:30px}
.ap-item{display:grid;grid-template-columns:74px 1fr;gap:20px;padding:32px 8px;border-top:1px solid var(--line);transition:transform .4s var(--ease),background .3s}
.ap-item:last-child{border-bottom:1px solid var(--line)}
.ap-item:hover{transform:translateX(10px)}
.ap-num{font-family:var(--font-display);font-size:1.5rem;color:var(--accent);font-weight:700}
.ap-item h3{font-size:1.3rem;margin-bottom:8px;font-weight:800}
html[data-theme="noir"] .ap-item h3{font-weight:600}
.ap-item p{color:var(--muted);font-size:.95rem;max-width:480px}
.ap-tag{display:inline-block;margin-top:14px;font-size:.7rem;font-weight:800;letter-spacing:.16em;text-transform:uppercase;color:var(--accent);border:1px solid var(--accent);border-radius:999px;padding:.3rem .8rem}
/* ============ WORKS ============ */
#works{padding:110px 0 60px}
.sec-head{display:flex;justify-content:space-between;align-items:flex-end;gap:30px;margin-bottom:70px;flex-wrap:wrap}
.sec-head h2{font-size:clamp(2rem,4.2vw,3.4rem);font-weight:900;margin-top:18px;line-height:1.05}
html[data-theme="noir"] .sec-head h2{font-weight:600}
html[data-theme="pulse"] .sec-head h2{font-size:clamp(1.5rem,3.4vw,2.5rem)}
.sec-sub{color:var(--muted);max-width:320px;font-size:.95rem}
.works-grid{display:grid;grid-template-columns:repeat(12,1fr);gap:28px;padding-bottom:80px}
.wc{background:var(--card);border:1px solid var(--line);border-radius:28px;overflow:hidden;box-shadow:0 20px 46px var(--shadow);transition:transform .5s var(--ease),box-shadow .5s}
.wc:nth-child(1){grid-column:1/6;transform:rotate(-2.2deg)}
.wc:nth-child(2){grid-column:6/13;transform:rotate(1.6deg) translateY(52px)}
.wc:nth-child(3){grid-column:2/7;transform:rotate(-1.8deg) translateY(-6px)}
.wc:nth-child(4){grid-column:7/12;transform:rotate(2.2deg) translateY(44px)}
.wc:hover{transform:rotate(0deg) translateY(-8px);box-shadow:0 34px 70px var(--shadow)}
.wc-img{overflow:hidden}
.wc-img img{width:100%;aspect-ratio:4/5;object-fit:cover;transition:transform .9s var(--ease)}
.wc:nth-child(2) .wc-img img{aspect-ratio:16/10}
.wc:hover .wc-img img{transform:scale(1.06)}
.wc-cap{display:flex;justify-content:space-between;align-items:center;gap:12px;padding:18px 22px}
.wc-cap h3{font-size:1.08rem;font-weight:800}
html[data-theme="noir"] .wc-cap h3{font-weight:600}
.wc-tag{font-size:.7rem;font-weight:800;letter-spacing:.14em;text-transform:uppercase;color:var(--muted);white-space:nowrap}
.wc-typo{background:var(--accent);color:var(--accent-ink);border:none;display:flex;flex-direction:column;justify-content:space-between;padding:30px;min-height:340px}
.wc-typo .wc-idx{font-size:.75rem;letter-spacing:.2em;opacity:.8}
.wc-typo h3{font-size:clamp(1.8rem,2.8vw,2.6rem);line-height:1.05;font-weight:900}
html[data-theme="noir"] .wc-typo h3{font-weight:600}
.wc-typo p{opacity:.85;font-size:.92rem;margin-top:10px}
.wc-typo .wc-arrow{width:56px;height:56px;border-radius:50%;border:2px solid var(--accent-ink);display:grid;place-items:center;font-size:1.3rem;transition:transform .4s var(--ease),background .3s,color .3s}
.wc-typo .wc-arrow:hover{transform:rotate(45deg);background:var(--accent-ink);color:var(--accent)}
/* ============ VOICE ============ */
#voice{background:var(--bg2);padding:110px 0;transition:background .5s}
.voice-in{max-width:880px;margin-inline:auto;text-align:center;position:relative}
.quote-mark{font-family:var(--font-display);font-size:6rem;line-height:.6;color:var(--accent);display:block;margin-bottom:26px}
#qText{font-family:var(--font-display);font-size:clamp(1.35rem,2.9vw,2.15rem);line-height:1.45;font-weight:600}
html[data-theme="pulse"] #qText{font-weight:700;font-size:clamp(1.05rem,2.3vw,1.7rem)}
.q-author{margin-top:30px;color:var(--muted);font-size:.92rem}
.q-author b{color:var(--ink)}
/* ============ CTA ============ */
#contact{background:var(--ink);color:var(--bg);padding:150px 0;position:relative;overflow:hidden;transition:background .5s}
.cta-blob{position:absolute;width:560px;height:560px;background:var(--accent);opacity:.22;filter:blur(80px);border-radius:50%;top:-160px;right:-120px;animation:morph 10s ease-in-out infinite alternate}
.cta-blob.b2{width:380px;height:380px;background:var(--accent2);opacity:.18;top:auto;right:auto;bottom:-140px;left:-100px;animation-delay:-4s}
.cta-in{position:relative;text-align:center}
.cta-in h2{font-size:clamp(2.3rem,6vw,4.8rem);line-height:1.05;font-weight:900;max-width:900px;margin:0 auto 40px}
html[data-theme="noir"] .cta-in h2{font-weight:600}
html[data-theme="pulse"] .cta-in h2{font-size:clamp(1.6rem,4.4vw,3.3rem)}
.cta-note{margin-top:26px;opacity:.65;font-size:.9rem}
/* ============ FOOTER ============ */
footer{background:var(--bg);padding:90px 0 40px;transition:background .5s}
.foot-mark{font-family:var(--font-display);font-size:clamp(4.6rem,19vw,15rem);line-height:.9;font-weight:900;letter-spacing:-.02em;display:flex;align-items:baseline;gap:.06em}
html[data-theme="noir"] .foot-mark{font-weight:600}
.foot-mark .suffix{font-size:.22em;color:var(--accent);letter-spacing:.06em;font-weight:700}
.foot-grid{display:grid;grid-template-columns:1.4fr 1fr 1fr;gap:44px;margin:60px 0 70px}
.foot-grid h4{font-size:.72rem;letter-spacing:.24em;text-transform:uppercase;color:var(--muted);margin-bottom:18px}
.foot-grid p{color:var(--muted);max-width:340px;font-size:.95rem}
.foot-grid ul{list-style:none}
.foot-grid li{margin-bottom:10px}
.foot-grid a{font-weight:600;font-size:.95rem;transition:color .3s}
.foot-grid a:hover{color:var(--accent)}
.foot-bottom{border-top:1px solid var(--line);padding-top:26px;display:flex;align-items:center;justify-content:space-between;gap:20px;flex-wrap:wrap;font-size:.85rem;color:var(--muted)}
#toTop{width:54px;height:54px;border-radius:50%;border:1px solid var(--line);background:transparent;color:var(--ink);font-size:1.1rem;transition:background .3s,color .3s,transform .3s}
#toTop:hover{background:var(--accent);color:var(--accent-ink);border-color:var(--accent);transform:translateY(-4px)}
/* ============ THEME DOCK ============ */
.dock{position:fixed;left:50%;bottom:18px;transform:translateX(-50%);z-index:320;display:flex;align-items:center;gap:4px;background:var(--card);border:1px solid var(--line);border-radius:999px;padding:6px;box-shadow:0 16px 40px var(--shadow)}
.dock-label{font-size:.68rem;font-weight:800;letter-spacing:.2em;text-transform:uppercase;color:var(--muted);padding:0 10px 0 12px;border-right:1px solid var(--line);margin-right:4px}
.dock button{display:flex;align-items:center;padding:9px 11px;border-radius:999px;border:0;background:transparent;color:var(--ink);transition:background .3s}
.dock button:hover,.dock button.on{background:var(--bg2)}
.dock .dot{width:14px;height:14px;border-radius:50%;background:var(--c);box-shadow:inset 0 0 0 1px rgba(0,0,0,.18);flex:none}
.dock .name{max-width:0;opacity:0;overflow:hidden;white-space:nowrap;font-weight:800;font-size:.8rem;transition:max-width .4s var(--ease),opacity .3s,margin .4s}
.dock button:hover .name,.dock button.on .name{max-width:120px;opacity:1;margin-left:8px}
/* ============ KEYFRAMES ============ */
@keyframes morph{0%{border-radius:58% 42% 63% 37%/46% 54% 46% 54%}50%{border-radius:45% 55% 38% 62%/58% 42% 60% 40%}100%{border-radius:60% 40% 52% 48%/42% 58% 44% 56%}}
@keyframes kenburns{from{transform:scale(1)}to{transform:scale(1.13) translate(2%,-2%)}}
@keyframes spin{to{transform:rotate(360deg)}}
@keyframes floaty{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}
@keyframes pulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.5);opacity:.5}}
@keyframes mqmove{to{transform:translateX(-50%)}}
/* ============ RESPONSIVE ============ */
@media(max-width:960px){
  .main-nav,.header-cta{display:none}
  .burger{display:block}
  .hero{padding-top:130px}
  .hero-grid{grid-template-columns:1fr;gap:70px}
  .hero-visual{width:min(440px,88vw)}
  .stats{grid-template-columns:repeat(2,1fr)}
  .ap-grid{grid-template-columns:1fr;gap:40px}
  .ap-left{position:static}
  .works-grid{grid-template-columns:1fr;gap:34px}
  .wc:nth-child(n){grid-column:1/-1;transform:none}
  .wc:nth-child(2) .wc-img img{aspect-ratio:4/5}
  .foot-grid{grid-template-columns:1fr;gap:34px}
  footer{padding-bottom:120px}
}
@media(max-width:560px){
  .dock-label{display:none}
  .stamp{width:92px;height:92px;top:-24px;right:-10px}
  .stamp svg{width:74px;height:74px}
  .hero-badge{left:-6px}
  .hero-actions{gap:18px}
  .stats{gap:18px}
}
/* ============ REDUCED MOTION ============ */
@media (prefers-reduced-motion: reduce){
  html{scroll-behavior:auto}
  *,*::before,*::after{animation-duration:.01ms !important;animation-iteration-count:1 !important;transition-duration:.01ms !important}
  .reveal{opacity:1 !important;transform:none !important}
  .mq-inner{animation:none !important;flex-wrap:wrap}
}
`

// ── HTML body ─────────────────────────────────────────────────────────────────
const ORGANIC_LANDING_HTML_BODY = `
<div id="progress"></div>
<div class="noise" aria-hidden="true"></div>
<div id="wipe" aria-hidden="true"><div id="wipeBlob"></div></div>

<header id="header">
  <div class="container header-in">
    <a class="logo" href="#top" aria-label="BST — на главную">
      <span class="logo-blob">B</span>
      <span class="logo-txt"><strong>BST</strong><span id="brandSuffix">Botanica</span></span>
    </a>
    <nav class="main-nav" aria-label="Основная навигация">
      <a href="#manifest">Манифест</a>
      <a href="#approach">Подход</a>
      <a href="#works">Работы</a>
      <a href="#voice">Отзывы</a>
      <a href="#contact">Контакт</a>
    </nav>
    <div class="header-cta"><a class="btn btn-sm magnet" href="#contact">Связаться</a></div>
    <button class="burger" id="burger" aria-label="Меню" aria-expanded="false"><span></span><span></span></button>
  </div>
</header>

<nav id="mnav" aria-label="Мобильное меню">
  <ul>
    <li><a class="mlink" href="#manifest"><i>01</i>Манифест</a></li>
    <li><a class="mlink" href="#approach"><i>02</i>Подход</a></li>
    <li><a class="mlink" href="#works"><i>03</i>Работы</a></li>
    <li><a class="mlink" href="#voice"><i>04</i>Отзывы</a></li>
    <li><a class="mlink" href="#contact"><i>05</i>Контакт</a></li>
  </ul>
  <div class="mnav-foot">hello@bst.studio · 4 бренда, одна дизайн-система</div>
</nav>

<main id="top">

  <!-- HERO -->
  <section class="hero">
    <div class="container hero-grid">
      <div class="hero-copy">
        <p class="eyebrow reveal" id="heroEyebrow"></p>
        <h1 class="reveal" id="heroTitle" style="transition-delay:.08s"></h1>
        <p class="lead reveal" id="heroLead" style="transition-delay:.16s"></p>
        <div class="hero-actions reveal" style="transition-delay:.24s">
          <a class="btn magnet" id="heroBtn" href="#contact"></a>
          <a class="link-arrow" id="heroLink" href="#works"></a>
        </div>
        <div class="chips reveal" id="chips" style="transition-delay:.32s"></div>
      </div>
      <div class="hero-visual reveal" style="transition-delay:.15s">
        <div class="deco-blob b1"></div>
        <div class="deco-blob b2"></div>
        <div class="blob-frame"><img id="heroImg" alt="Визуал бренда BST"></div>
        <div class="stamp" aria-hidden="true">
          <svg viewBox="0 0 120 120">
            <defs><path id="circ" d="M60,60 m-45,0 a45,45 0 1,1 90,0 a45,45 0 1,1 -90,0"/></defs>
            <text><textPath href="#circ">BST ✦ ORGANIC  FLUID ✦ DESIGN ✦ STUDIO ✦</textPath></text>
            <circle cx="60" cy="60" r="5" fill="var(--accent)"/>
          </svg>
        </div>
        <div class="hero-badge" id="heroBadge"></div>
      </div>
    </div>
  </section>

  <!-- MARQUEE -->
  <div class="mq" aria-hidden="true">
    <div class="mq-inner">
      <div class="mq-track" id="mq1"></div>
      <div class="mq-track" id="mq2"></div>
    </div>
  </div>

  <!-- MANIFESTO -->
  <section id="manifest">
    <div class="container">
      <p class="eyebrow reveal" id="manEyebrow"></p>
      <p class="manifest-text reveal" id="manText" style="transition-delay:.1s"></p>
      <div class="stats reveal" id="stats" style="transition-delay:.18s"></div>
    </div>
  </section>

  <div class="wave w1" aria-hidden="true"><svg viewBox="0 0 1440 90" preserveAspectRatio="none"><path fill="currentColor" d="M0,50 C220,88 430,10 720,38 C1010,66 1220,18 1440,54 L1440,90 L0,90 Z"/></svg></div>

  <!-- APPROACH -->
  <section id="approach">
    <div class="container ap-grid">
      <div class="ap-left">
        <p class="eyebrow reveal" id="apEyebrow"></p>
        <h2 class="reveal" id="apTitle" style="transition-delay:.08s"></h2>
        <p class="ap-note reveal" id="apNote" style="transition-delay:.16s"></p>
        <a class="link-arrow reveal" href="#contact" style="transition-delay:.24s">Обсудить проект</a>
      </div>
      <div class="ap-list" id="apList"></div>
    </div>
  </section>

  <!-- WORKS -->
  <section id="works">
    <div class="container">
      <div class="sec-head reveal">
        <div>
          <p class="eyebrow" id="wkEyebrow"></p>
          <h2 id="wkTitle"></h2>
        </div>
        <p class="sec-sub" id="wkSub"></p>
      </div>
      <div class="works-grid" id="worksGrid"></div>
    </div>
  </section>

  <div class="wave w2" aria-hidden="true"><svg viewBox="0 0 1440 90" preserveAspectRatio="none"><path fill="currentColor" d="M0,44 C260,6 480,86 760,44 C1040,4 1240,70 1440,40 L1440,90 L0,90 Z"/></svg></div>

  <!-- VOICE -->
  <section id="voice">
    <div class="container voice-in reveal">
      <span class="quote-mark" aria-hidden="true">«</span>
      <blockquote id="qText"></blockquote>
      <p class="q-author" id="qAuthor"></p>
    </div>
  </section>

  <div class="wave w3" aria-hidden="true"><svg viewBox="0 0 1440 90" preserveAspectRatio="none"><path fill="currentColor" d="M0,52 C240,90 460,12 740,40 C1020,68 1220,22 1440,50 L1440,90 L0,90 Z"/></svg></div>

  <!-- CTA -->
  <section id="contact">
    <div class="cta-blob" aria-hidden="true"></div>
    <div class="cta-blob b2" aria-hidden="true"></div>
    <div class="container cta-in">
      <h2 class="reveal" id="ctaTitle"></h2>
      <a class="btn btn-big magnet reveal" id="ctaBtn" href="mailto:hello@bst.studio" style="transition-delay:.1s"></a>
      <p class="cta-note reveal" id="ctaNote" style="transition-delay:.18s"></p>
    </div>
  </section>

  <div class="wave w4" aria-hidden="true"><svg viewBox="0 0 1440 90" preserveAspectRatio="none"><path fill="currentColor" d="M0,46 C240,86 470,10 750,42 C1030,72 1230,20 1440,52 L1440,90 L0,90 Z"/></svg></div>
</main>

<!-- FOOTER -->
<footer>
  <div class="container">
    <div class="foot-mark reveal">BST<span class="suffix" id="footSuffix">/botanica</span></div>
    <div class="foot-grid">
      <div>
        <h4>Студия</h4>
        <p id="footTag"></p>
      </div>
      <div>
        <h4>Навигация</h4>
        <ul>
          <li><a href="#manifest">Манифест</a></li>
          <li><a href="#approach">Подход</a></li>
          <li><a href="#works">Работы</a></li>
          <li><a href="#contact">Контакт</a></li>
        </ul>
      </div>
      <div>
        <h4>Связь</h4>
        <ul>
          <li><a href="mailto:hello@bst.studio">hello@bst.studio</a></li>
          <li><a href="#top">Telegram ↗</a></li>
          <li><a href="#top">Behance ↗</a></li>
          <li><a href="#top">Instagram ↗</a></li>
        </ul>
      </div>
    </div>
    <div class="foot-bottom">
      <span>© <span id="year"></span> BST Studio — organic / fluid design system</span>
      <span>4 бренда · 1 код · ∞ вариаций</span>
      <button id="toTop" aria-label="Наверх">↑</button>
    </div>
  </div>
</footer>

<!-- THEME DOCK -->
<div class="dock" role="group" aria-label="Переключение бренда">
  <span class="dock-label">Бренд</span>
  <button data-key="botanica" style="--c:#C4623D" aria-label="BST Botanica"><span class="dot"></span><span class="name">Botanica</span></button>
  <button data-key="ocea" style="--c:#0F7E7B" aria-label="BST Océa"><span class="dot"></span><span class="name">Océa</span></button>
  <button data-key="noir" style="--c:#C9A15C" aria-label="BST Noir"><span class="dot"></span><span class="name">Noir</span></button>
  <button data-key="pulse" style="--c:#FF4D2A" aria-label="BST Pulse"><span class="dot"></span><span class="name">Pulse</span></button>
</div>
`

// ── Engine JS ─────────────────────────────────────────────────────────────────
function organicLandingEngineJs(brand: string, mode: 'canvas' | 'preview'): string {
  return `
/* ================= DATA: 4 BRANDS ================= */
const IMG = {
  botanica:{hero:"https://image.qwenlm.ai/public_source/79463931-7cce-4161-a2b6-0115053e572e/15bc57e0b-6b39-4bef-8d6c-b669ff85352b.png",w1:"https://image.qwenlm.ai/public_source/79463931-7cce-4161-a2b6-0115053e572e/1fdc4b253-85ee-477c-9789-ec4e79173e63.png",w2:"https://image.qwenlm.ai/public_source/79463931-7cce-4161-a2b6-0115053e572e/1ff252f06-8b6f-40de-8b6a-22a16d46d7f8.png",w3:"https://image.qwenlm.ai/public_source/79463931-7cce-4161-a2b6-0115053e572e/1e84c4d5b-1886-4d5e-bb69-87b147d242de.png"},
  ocea:{hero:"https://image.qwenlm.ai/public_source/79463931-7cce-4161-a2b6-0115053e572e/18637ca5b-e068-49cf-95ca-62158d9bd4f8.png",w1:"https://image.qwenlm.ai/public_source/79463931-7cce-4161-a2b6-0115053e572e/1768ae0c7-cb63-46d4-86c6-e130e9756532.png",w2:"https://image.qwenlm.ai/public_source/79463931-7cce-4161-a2b6-0115053e572e/195a87002-456e-462c-8e03-6e83aba657a7.png",w3:"https://image.qwenlm.ai/public_source/79463931-7cce-4161-a2b6-0115053e572e/125ee3a4b-b171-428a-9f38-1dddd90b6cb4.png"},
  noir:{hero:"https://image.qwenlm.ai/public_source/79463931-7cce-4161-a2b6-0115053e572e/185f76ce9-81d5-4216-8298-38551e304510.png",w1:"https://image.qwenlm.ai/public_source/79463931-7cce-4161-a2b6-0115053e572e/10fc666ac-2f02-41b5-968d-358d45fbd3d2.png",w2:"https://image.qwenlm.ai/public_source/79463931-7cce-4161-a2b6-0115053e572e/1a277b30d-56f6-4a99-9d59-33cc4ca84c88.png",w3:"https://image.qwenlm.ai/public_source/79463931-7cce-4161-a2b6-0115053e572e/192ce85e0-677c-499b-8d6b-252fb8b85628.png"},
  pulse:{hero:"https://image.qwenlm.ai/public_source/79463931-7cce-4161-a2b6-0115053e572e/14861a972-b58a-46dc-8e4a-a3c384d2498e.png",w1:"https://image.qwenlm.ai/public_source/79463931-7cce-4161-a2b6-0115053e572e/135e44f93-095f-4dfc-b552-f1a63f158f51.png",w2:"https://image.qwenlm.ai/public_source/79463931-7cce-4161-a2b6-0115053e572e/10a663671-bc9e-4bfe-a9a4-33c68a4e2ed5.png",w3:"https://image.qwenlm.ai/public_source/79463931-7cce-4161-a2b6-0115053e572e/15eefd6af-eb10-4123-96d8-dcb58703835c.png"}
};
const BRANDS = {
  botanica:{
    name:"Botanica", title:"BST Botanica — органическая косметика", meta:"#F4EFE4",
    hero:{eyebrow:"BST · Botanica — органическая косметика",
      title:"Косметика, написанная <em>самой природой</em>",
      lead:"BST Botanica создаёт уход на основе живых экстрактов: без сульфатов, силиконов и спешки. Только то, что земля отдаёт добровольно.",
      badge:"Новая коллекция «Тихий сад»", ctaBtn:"Выбрать ритуал", ctaLink:"Смотреть работы ↓",
      chips:["100% Vegan","Cold-pressed","Zero waste","Cruelty-free"]},
    marquee:["100% органика","Vegan-формулы","Перерабатываемая упаковка","Без жестоких тестов","Сырьё малых ферм","Холодный отжим"],
    manifest:{eyebrow:"Манифест", text:"Мы верим, что кожа читает состав как поэму. Поэтому в каждой баночке — только то, что можно <em>произнести вслух</em>: травы, масла и глина, собранные на рассвете.",
      stats:[{n:97,s:"%",l:"ингредиентов природного происхождения"},{n:24,s:"",l:"малых фермы поставляют сырьё"},{n:0,s:"",l:"синтетических отдушек и красителей"},{n:12,s:" лет",l:"заботы о вашей коже"}]},
    approach:{eyebrow:"Подход", title:"Как рождается баночка", note:"Четыре принципа, которые не обсуждаются и не удешевляются.",
      items:[{n:"01",t:"Живые формулы",d:"Активы холодного отжима сохраняют то, что убивает горячая экстракция: витамины, энзимы, характер.",tag:"Лаборатория"},
        {n:"02",t:"Честный состав",d:"Каждый ингредиент назван по имени и с адресом фермы. Скрытых «и др.» у нас не бывает.",tag:"Прозрачность"},
        {n:"03",t:"Цикличная упаковка",d:"Стекло, алюминий и компостируемые этикетки. Баночку можно вернуть — и получить скидку.",tag:"Zero waste"},
        {n:"04",t:"Ритуалы ухода",d:"Мы продаём не продукты, а сценарии: утро, вечер и десять минут тишины между ними.",tag:"Опыт"}]},
    works:{eyebrow:"Работы", title:"Избранное из оранжереи", sub:"Три линейки, которые принесли нам полку наград и миллион повторных покупок.",
      items:[{img:"w1",t:"Сыворотка №7 «Росток»",tag:"Продукт"},{img:"w2",t:"Маска из зелёной глины",tag:"Текстура"},{img:"w3",t:"Линия «Тихий сад»",tag:"Коллекция"}],
      typo:{word:"Пахнет утром в саду", sub:"Фирменный аромат бренда — роса, овёс и эвкалипт."}},
    quote:{text:"Баночка BST Botanica пахнет утром в саду — это лучшая рецензия на нашу работу.", author:"<b>Марина Ковалёва</b> · косметолог, стаж 14 лет"},
    cta:{title:"Попробуйте природу на коже", btn:"Выбрать ритуал", note:"Доставка в крафтовой упаковке · образец — в каждом заказе"}
  },
  ocea:{
    name:"Océa", title:"BST Océa — функциональные напитки", meta:"#EAF4F2",
    hero:{eyebrow:"BST · Océa — функциональные напитки",
      title:"Глоток, который <em>меняет ритм</em> дня",
      lead:"Océa — вода глубокой фильтрации с минералами и адаптогенами. Ноль сахара, ноль компромиссов, максимум фокуса.",
      badge:"Лимитка с цитроном — уже здесь", ctaBtn:"Найти свой ритм", ctaLink:"Коллекция вкусов ↓",
      chips:["0 сахара","Адаптогены","Mg + B12","Ocean plastic"]},
    marquee:["Глубинная вода","Магний и B12","Ноль сахара","Бутылка из океанического пластика","Создано для фокуса","72 часа фильтрации"],
    manifest:{eyebrow:"Манифест", text:"Вода — это инфраструктура внимания. Мы строим её так же тщательно, как мосты: <em>семьдесят два часа</em> фильтрации и ни одной пустой калории.",
      stats:[{n:0,s:"",l:"сахара во всей линейке"},{n:6,s:"",l:"минералов в каждой капле"},{n:72,s:" ч",l:"естественной фильтрации"},{n:3,s:"",l:"вкуса в постоянной коллекции"}]},
    approach:{eyebrow:"Подход", title:"Инженерия свежести", note:"Напиток проектируется как продукт: с метриками, исследованиями и уважением к телу.",
      items:[{n:"01",t:"Глубокая фильтрация",d:"Вода проходит ледниковую морену и мембраны, сохраняя природную структуру минералов.",tag:"Источник"},
        {n:"02",t:"Функциональные формулы",d:"Focus, Calm и Rise: адаптогены в рабочих дозировках, а не для этикетки.",tag:"Наука"},
        {n:"03",t:"Осознанная упаковка",d:"Бутылка на 100% из выловленного океанического пластика. Этикетка растворяется при переработке.",tag:"循环"},
        {n:"04",t:"Ритм приёма",d:"Утро — Rise, день — Focus, вечер — Calm. Подписка собирается под ваш календарь.",tag:"Привычка"}]},
    works:{eyebrow:"Работы", title:"Коллекция ритмов", sub:"Три состояния в алюминиевой банке: фокус, спокойствие и заряд.",
      items:[{img:"w1",t:"Océa Focus",tag:"Флагман"},{img:"w2",t:"Глубинная серия",tag:"Источник"},{img:"w3",t:"Лимитка с цитроном",tag:"Limited"}],
      typo:{word:"Пейте осмысленно", sub:"Каждая партия — с QR-паспортом источника и датой розлива."}},
    quote:{text:"Océa стоит на столе каждой планёрки — команда стала заметно спокойнее и заметно быстрее.", author:"<b>Артём Двинин</b> · продакт-директор"},
    cta:{title:"Найдите свой ритм", btn:"Заказать набор", note:"Первая доставка — бесплатно · отмена подписки в один тап"}
  },
  noir:{
    name:"Noir", title:"BST Noir — дом тихой роскоши", meta:"#131117",
    hero:{eyebrow:"BST · Noir — дом тихой роскоши",
      title:"Тишина — <em>новая роскошь</em>",
      lead:"Noir — ателье, где форма важнее логотипа. Кашемир, шёлк и ручной крой для тех, кто не повышает голос.",
      badge:"Запись на примерку открыта", ctaBtn:"Забронировать визит", ctaLink:"Коллекции ателье ↓",
      chips:["Ручной крой","Кашемир grade A","Made to measure"]},
    marquee:["Ручная работа","Лимитированные серии","Ателье в Москве","Кашемир малых мануфактур","Пошив по мерке","Ремонт на всю жизнь"],
    manifest:{eyebrow:"Манифест", text:"Мы не гонимся за сезоном. Одна линия в год, двадцать один день на костюм и <em>ни одной</em> случайной строчки.",
      stats:[{n:18,s:"",l:"портных в ателье"},{n:100,s:"%",l:"ручная отделка швов"},{n:21,s:" день",l:"на made-to-measure"},{n:1,s:"",l:"ателье, один адрес"}]},
    approach:{eyebrow:"Подход", title:"Церемония кроя", note:"Вещь начинается с разговора и заканчивается тогда, когда её невозможно улучшить.",
      items:[{n:"01",t:"Индивидуальный крой",d:"Лекала строятся по 34 меркам и хранятся вечно: следующий костюм сошьют без примерки.",tag:"Ателье"},
        {n:"02",t:"Ткани малых мануфактур",d:"Кашемир и шёлк с фабрик, где станкам больше лет, чем брендам массовой моды.",tag:"Материя"},
        {n:"03",t:"Примерка как ритуал",d:"Чай, тишина и час времени. Мы не продаём — мы одеваем.",tag:"Сервис"},
        {n:"04",t:"Уход на всю жизнь",d:"Любая вещь Noir бесплатно чинится и обновляется. Всегда.",tag:"Гарантия"}]},
    works:{eyebrow:"Работы", title:"Архив тишины", sub:"Вещи, которые не нужно представлять — их достаточно надеть.",
      items:[{img:"w1",t:"Пальто «Полночь»",tag:"Кутюр"},{img:"w2",t:"Ручной шов",tag:"Ремесло"},{img:"w3",t:"Линия «Антрацит»",tag:"Аксессуары"}],
      typo:{word:"Форма важнее логотипа", sub:"Ни одной видимой этикетки — только внутренний штамп мастера."}},
    quote:{text:"Здесь не продают одежду — здесь возвращают достоинство вещи.", author:"<b>Элеонора Штерн</b> · стилист"},
    cta:{title:"Запишитесь на примерку тишины", btn:"Забронировать визит", note:"Один визит в день · чай и тишина включены"}
  },
  pulse:{
    name:"Pulse", title:"BST Pulse — фестиваль креативных индустрий", meta:"#FFF3E6",
    hero:{eyebrow:"BST · Pulse — фестиваль креативных индустрий",
      title:"Энергия — наш <em>главный материал</em>",
      lead:"Pulse собирает дизайнеров, музыкантов и технарей на три дня, чтобы делать невозможное — громко, ярко и вместе.",
      badge:"Open call до 30 июня", ctaBtn:"Взять билет", ctaLink:"Программа фестиваля ↓",
      chips:["72 часа нон-стоп","120 спикеров","Open call"]},
    marquee:["Лекции и воркшопы","Живые перформансы","Ночной маркет","Open call для студентов","Afterparty до рассвета","Сцена у воды"],
    manifest:{eyebrow:"Манифест", text:"Фестиваль — это реактор: мы смешиваем дисциплины, пока не начнётся <em>цепная реакция</em> новых проектов.",
      stats:[{n:12000,s:"",l:"участников прошлого года"},{n:120,s:"",l:"спикеров и артистов"},{n:72,s:" ч",l:"нон-стоп программы"},{n:1,s:"",l:"большая сцена у воды"}]},
    approach:{eyebrow:"Подход", title:"Как собирается вайб", note:"Четыре цеха фестиваля, которые работают как один организм.",
      items:[{n:"01",t:"Программа и курирование",d:"Лекции, баттлы и шоукейсы отбирают кураторы индустрий, а не продюсеры сцен.",tag:"Контент"},
        {n:"02",t:"Сценография",d:"Органические формы, переработанный пластик и свет, который дышит вместе с толпой.",tag:"Пространство"},
        {n:"03",t:"Комьюнити и open call",d:"Треть программы собирают заявки студентов и независимых команд.",tag:"Люди"},
        {n:"04",t:"Бренд-партнёрства",d:"Партнёры приходят с активностями, а не с роллапами. Вайб дороже логотипа.",tag:"Партнёры"}]},
    works:{eyebrow:"Работы", title:"Хроника заряда", sub:"Три сцены прошлого сезона, которые вы могли видеть из толпы.",
      items:[{img:"w1",t:"Толпа на закате",tag:"Комьюнити"},{img:"w2",t:"Главная арка",tag:"Сценография"},{img:"w3",t:"Афиши Pulse‑24",tag:"Айдентика"}],
      typo:{word:"Громче. Ярче. Вместе.", sub:"Айдентика фестиваля собрана на живых флюид-формах."}},
    quote:{text:"Pulse — это три дня, после которых хочется уволиться и делать своё. Я осталась.", author:"<b>Соня Литвинова</b> · арт-директор"},
    cta:{title:"Заряжайтесь с нами", btn:"Взять билет", note:"Ранние птицы — минус 40% · студентам по open call — бесплатно"}
  }
};

/* ================= HELPERS ================= */
const $ = s => document.querySelector(s);
const html = document.documentElement;
const RM = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
let current = null;

function renderChips(b){
  $("#chips").innerHTML = b.hero.chips.map(c=>\`<span class="chip">\${c}</span>\`).join("");
}
function renderMarquee(b){
  const htmlStr = b.marquee.map(t=>\`<span>\${t}</span><i>✦</i>\`).join("");
  $("#mq1").innerHTML = htmlStr; $("#mq2").innerHTML = htmlStr;
}
function renderStats(b){
  $("#stats").innerHTML = b.manifest.stats.map(s=>\`
    <div class="stat"><div class="stat-n"><span class="count" data-to="\${s.n}">0</span>\${s.s}</div><div class="stat-l">\${s.l}</div></div>\`).join("");
  bindCounts($("#stats"));
}
function renderApproach(b){
  $("#apList").innerHTML = b.approach.items.map((it,i)=>\`
    <div class="ap-item reveal in" style="transition-delay:\${i*60}ms">
      <div class="ap-num">\${it.n}</div>
      <div><h3>\${it.t}</h3><p>\${it.d}</p><span class="ap-tag">\${it.tag}</span></div>
    </div>\`).join("");
}
function renderWorks(b){
  const cards = b.works.items.map((w,i)=>\`
    <article class="wc reveal in" style="transition-delay:\${i*80}ms">
      <div class="wc-img"><img src="\${IMG[currentKey][w.img]}" alt="\${w.t}" loading="lazy"></div>
      <div class="wc-cap"><h3>\${w.t}</h3><span class="wc-tag">\${w.tag}</span></div>
    </article>\`).join("");
  const typo = \`
    <article class="wc wc-typo reveal in" style="transition-delay:240ms">
      <span class="wc-idx">BST / \${currentKey.toUpperCase()}</span>
      <div><h3>\${b.works.typo.word}</h3><p>\${b.works.typo.sub}</p></div>
      <a class="wc-arrow" href="#contact" aria-label="Обсудить проект">→</a>
    </article>\`;
  $("#worksGrid").innerHTML = cards + typo;
}
let currentKey = "botanica";

/* counters */
const countObs = new IntersectionObserver(es=>{
  es.forEach(e=>{ if(e.isIntersecting){ runCount(e.target); countObs.unobserve(e.target);} });
},{threshold:.4});
function bindCounts(scope){ scope.querySelectorAll(".count").forEach(el=>countObs.observe(el)); }
function runCount(el){
  const to = +el.dataset.to;
  if(RM || to === 0){ el.textContent = to; return; }
  const t0 = performance.now(), dur = 1400;
  (function tick(t){
    const p = Math.min(1,(t-t0)/dur), e = 1-Math.pow(1-p,3);
    el.textContent = Math.round(to*e);
    if(p<1) requestAnimationFrame(tick);
  })(t0);
}

/* reveal observer */
const revObs = new IntersectionObserver(es=>{
  es.forEach(e=>{ if(e.isIntersecting){ e.target.classList.add("in"); revObs.unobserve(e.target);} });
},{threshold:.12});

/* ================= APPLY BRAND ================= */
function applyBrand(key){
  currentKey = key;
  const b = BRANDS[key];
  html.dataset.theme = key;
  document.title = b.title;
  $("#metaTheme") && $("#metaTheme").setAttribute("content", b.meta);
  $("#brandSuffix").textContent = b.name;
  $("#heroEyebrow").textContent = b.hero.eyebrow;
  $("#heroTitle").innerHTML = b.hero.title;
  $("#heroLead").textContent = b.hero.lead;
  $("#heroBadge").textContent = b.hero.badge;
  $("#heroBtn").textContent = b.hero.ctaBtn;
  $("#heroLink").textContent = b.hero.ctaLink;
  $("#manEyebrow").textContent = b.manifest.eyebrow;
  $("#manText").innerHTML = b.manifest.text;
  $("#apEyebrow").textContent = b.approach.eyebrow;
  $("#apTitle").textContent = b.approach.title;
  $("#apNote").textContent = b.approach.note;
  $("#wkEyebrow").textContent = b.works.eyebrow;
  $("#wkTitle").textContent = b.works.title;
  $("#wkSub").textContent = b.works.sub;
  $("#qText").textContent = "«" + b.quote.text + "»";
  $("#qAuthor").innerHTML = b.quote.author;
  $("#ctaTitle").textContent = b.cta.title;
  $("#ctaBtn").textContent = b.cta.btn;
  $("#ctaNote").textContent = b.cta.note;
  $("#footSuffix").textContent = "/" + key;
  $("#footTag").textContent = "BST — органические формы, живые градиенты и текучая типографика. Одна дизайн-система, четыре характера: " + Object.values(BRANDS).map(x=>x.name).join(", ") + ".";
  renderChips(b); renderMarquee(b); renderStats(b); renderApproach(b); renderWorks(b);
  /* hero image with fade */
  const img = $("#heroImg");
  img.classList.add("fade");
  const pre = new Image();
  pre.onload = ()=>{ img.src = IMG[key].hero; img.classList.remove("fade"); };
  pre.src = IMG[key].hero;
  /* dock state */
  document.querySelectorAll(".dock button").forEach(btn=>{
    const on = btn.dataset.key === key;
    btn.classList.toggle("on", on);
    btn.setAttribute("aria-pressed", on);
  });
  current = key;
}

/* theme wipe transition */
function setTheme(key){
  if(key === current) return;
  const blob = $("#wipeBlob");
  blob.style.background = getComputedStyle(html).getPropertyValue("--ink").trim();
  $("#wipe").classList.add("on");
  setTimeout(()=>applyBrand(key), 400);
  setTimeout(()=>$("#wipe").classList.remove("on"), 860);
}
document.querySelectorAll(".dock button").forEach(btn=>{
  btn.addEventListener("click", ()=>setTheme(btn.dataset.key));
});

/* ================= UI BEHAVIORS ================= */
const header = $("#header");
addEventListener("scroll", ()=>{
  header.classList.toggle("scrolled", scrollY > 30);
  const h = document.documentElement;
  $("#progress").style.width = (scrollY/(h.scrollHeight-innerHeight)*100)+"%";
},{passive:true});

const burger = $("#burger");
burger.addEventListener("click", ()=>{
  const open = html.classList.toggle("menu-open");
  burger.setAttribute("aria-expanded", open);
  document.body.style.overflow = open ? "hidden" : "";
});
document.querySelectorAll("#mnav a").forEach(a=>a.addEventListener("click", ()=>{
  html.classList.remove("menu-open"); document.body.style.overflow="";
  burger.setAttribute("aria-expanded","false");
}));

$("#toTop").addEventListener("click", ()=>scrollTo({top:0, behavior: RM ? "auto" : "smooth"}));
$("#year").textContent = new Date().getFullYear();

/* magnetic buttons */
if(!RM && matchMedia("(pointer:fine)").matches){
  document.querySelectorAll(".magnet").forEach(el=>{
    el.addEventListener("mousemove", e=>{
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left - r.width/2)/r.width;
      const y = (e.clientY - r.top - r.height/2)/r.height;
      el.style.transform = \`translate(\${x*14}px, \${y*10}px)\`;
    });
    el.addEventListener("mouseleave", ()=>{ el.style.transform = ""; });
  });
}

/* ================= INIT ================= */
applyBrand('${brand}');
document.querySelectorAll(".reveal").forEach(el=>revObs.observe(el));
/* preload all brand imagery */
setTimeout(()=>{ Object.values(IMG).forEach(set=>Object.values(set).forEach(src=>{ const i=new Image(); i.src=src; })); }, 1200);
${mode === 'canvas' ? `(function(){
  document.querySelectorAll('.reveal,[data-rv],.rv').forEach(function(el){el.classList.add('in');});
  document.querySelectorAll('[data-count]').forEach(function(el){var t=parseFloat(el.dataset.count),d=+(el.dataset.dec||0);el.textContent=d?t.toFixed(d).replace('.',','):Math.round(t).toLocaleString('ru-RU');});
  document.body.classList.add('loaded','is-ready');
  document.addEventListener('click',function(e){var a=e.target.closest('a[href]');if(a)e.preventDefault();},true);
  document.querySelectorAll('form').forEach(function(f){f.addEventListener('submit',function(e){e.preventDefault();});});
})();` : ''}
`
}

// ── Public API ────────────────────────────────────────────────────────────────
export function getOrganicLandingPageHtml(data: OrganicLandingPageData, mode: 'canvas' | 'preview'): string {
  const brand = data.brand ?? 'botanica'
  return `<!DOCTYPE html>
<html lang="ru" data-theme="${brand}">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<meta name="theme-color" id="metaTheme" content="#F4EFE4">
<title>BST Botanica — органическая косметика</title>
<link rel="icon" href="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cpath d='M50 4C76 2 96 22 96 50c0 28-20 46-46 46S4 78 4 50 24 6 50 4z' fill='%23C4623D'/%3E%3Ctext x='50' y='68' font-size='52' text-anchor='middle' font-family='Georgia,serif' fill='%23FFF8F0'%3EB%3C/text%3E%3C/svg%3E">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/fraunces@latest/latin-400-normal.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/fraunces@latest/latin-600-normal.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/fraunces@latest/latin-600-italic.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/fraunces@latest/latin-900-normal.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/manrope@latest/latin-400-normal.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/manrope@latest/latin-500-normal.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/manrope@latest/latin-700-normal.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/manrope@latest/latin-800-normal.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/sora@latest/latin-400-normal.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/sora@latest/latin-600-normal.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/sora@latest/latin-700-normal.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/sora@latest/latin-800-normal.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/cormorant-garamond@latest/latin-500-normal.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/cormorant-garamond@latest/latin-600-normal.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/cormorant-garamond@latest/latin-600-italic.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/cormorant-garamond@latest/latin-700-normal.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/jost@latest/latin-300-normal.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/jost@latest/latin-400-normal.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/jost@latest/latin-500-normal.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/space-grotesk@latest/latin-400-normal.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/space-grotesk@latest/latin-500-normal.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/space-grotesk@latest/latin-700-normal.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/unbounded@latest/latin-500-normal.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/unbounded@latest/latin-700-normal.css" rel="stylesheet">
<link href="https://cdn.jsdelivr.net/fontsource/fonts/unbounded@latest/latin-900-normal.css" rel="stylesheet">
<style>${ORGANIC_LANDING_CSS}</style>
</head>
<body>
${ORGANIC_LANDING_HTML_BODY}
<script>${organicLandingEngineJs(brand, mode)}</script>
</body>
</html>`
}
